"use client";

import { motion } from "framer-motion";
import { useState } from "react";
import { useLanguageVariables } from "@/contexts/LanguageVariablesContext";

export interface YachtMonthlyPrice {
    month: number; // 1-12 (Ocak-Aralık)
    monthName: string; // "May", "June" gibi
    monthNameFull: string; // "Mayıs", "Haziran" gibi
    price: number; // Günlük fiyat
    currency: string; // "EUR", "USD" gibi
    isAvailable: boolean; // Bu ay müsait mi?
}

/** Kiralama tipi: fiyat etiketlemesi (günlük / haftalık / aylık) */
export type RentalType = 'daily' | 'weekly' | 'monthly';

interface YachtPriceCalendarProps {
    prices: YachtMonthlyPrice[];
    currency?: string;
    onMonthSelect?: (month: number) => void;
    selectedMonth?: number | null;
    /** Kiralama tipi; weekly ise "Haftalık kiralama" açıklaması ve etiket kullanılır */
    rentalType?: RentalType | string;
}

const MONTHS = [
    { num: 1, name: 'Jan', nameFull: 'January', nameTR: 'Ocak' },
    { num: 2, name: 'Feb', nameFull: 'February', nameTR: 'Şubat' },
    { num: 3, name: 'Mar', nameFull: 'March', nameTR: 'Mart' },
    { num: 4, name: 'Apr', nameFull: 'April', nameTR: 'Nisan' },
    { num: 5, name: 'May', nameFull: 'May', nameTR: 'Mayıs' },
    { num: 6, name: 'Jun', nameFull: 'June', nameTR: 'Haziran' },
    { num: 7, name: 'Jul', nameFull: 'July', nameTR: 'Temmuz' },
    { num: 8, name: 'Aug', nameFull: 'August', nameTR: 'Ağustos' },
    { num: 9, name: 'Sep', nameFull: 'September', nameTR: 'Eylül' },
    { num: 10, name: 'Oct', nameFull: 'October', nameTR: 'Ekim' },
    { num: 11, name: 'Nov', nameFull: 'November', nameTR: 'Kasım' },
    { num: 12, name: 'Dec', nameFull: 'December', nameTR: 'Aralık' },
];

const formatPrice = (price: number, currency: string = 'EUR'): string => {
    const symbols: Record<string, string> = {
        'EUR': '€',
        'USD': '$',
        'GBP': '£',
        'TRY': '₺',
    };
    const symbol = symbols[currency.toUpperCase()] || currency;
    // Tour PriceDetailTable ile uyumlu: iki ondalık basamak
    return `${symbol} ${price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

export default function YachtPriceCalendar({ 
    prices, 
    currency = 'EUR',
    onMonthSelect,
    selectedMonth,
    rentalType = 'daily',
}: YachtPriceCalendarProps) {
    const [hoveredMonth, setHoveredMonth] = useState<number | null>(null);
    const { getVariable } = useLanguageVariables();
    const isWeekly = String(rentalType).toLowerCase() === 'weekly';
    const isMonthly = String(rentalType).toLowerCase() === 'monthly';

    // Mevcut ayı tespit et (1-12)
    const currentMonth = new Date().getMonth() + 1;

    // Tüm ayları oluştur, fiyatı olanları işaretle
    const allMonths = MONTHS.map(month => {
        const priceData = prices.find(p => p.month === month.num);
        return {
            ...month,
            price: priceData?.price || null,
            isAvailable: priceData?.isAvailable !== false && priceData?.price !== null && priceData?.price !== undefined && priceData.price > 0,
            hasPrice: priceData !== undefined,
            isCurrentMonth: month.num === currentMonth,
        };
    });

    // Müsait ayları filtrele (sadece fiyatı olanlar)
    const availableMonths = allMonths.filter(m => m.isAvailable);

    const handleMonthClick = (monthNum: number) => {
        if (onMonthSelect) {
            onMonthSelect(monthNum);
        }
    };

    return (
        <div className="space-y-6">
            <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded">
                <div className="flex items-start gap-3">
                    <svg className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div>
                        <p className="text-sm text-blue-800 font-medium mb-1">
                            {isWeekly
                                ? getVariable('yacht_price.weekly_rental_prices', 'Weekly Charter Prices')
                                : isMonthly
                                    ? getVariable('yacht_price.monthly_rental_prices', 'Monthly Rental Prices')
                                    : getVariable('yacht_price.daily_rental_prices', 'Daily Rental Prices')}
                        </p>
                        <p className="text-xs text-blue-700">
                            {isWeekly
                                ? getVariable('yacht_price.weekly_basis_description', 'Prices are per week. Minimum rental period is 7 days.')
                                : isMonthly
                                    ? getVariable('yacht_price.monthly_basis_description', 'Prices are on a monthly basis. Minimum rental period is specified in yacht features.')
                                    : getVariable('yacht_price.daily_basis_description', 'Prices are per day. Minimum rental period is specified in yacht features.')}
                        </p>
                    </div>
                </div>
            </div>

            <div>
                <h3 className="text-lg font-semibold text-alsero-dark-gray mb-4">
                    {getVariable('yacht_price.available_months', 'Available Months')} ({availableMonths.length} {getVariable('yacht_price.month', 'month')})
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                    {availableMonths.map((month) => {
                        const isSelected = selectedMonth === month.num;
                        const isHovered = hoveredMonth === month.num;
                        const isCurrent = month.isCurrentMonth;
                        
                        return (
                            <motion.button
                                type="button"
                                key={month.num}
                                onClick={() => handleMonthClick(month.num)}
                                onMouseEnter={() => setHoveredMonth(month.num)}
                                onMouseLeave={() => setHoveredMonth(null)}
                                className={`
                                    relative p-3 rounded-xl border-2 transition-all duration-300 overflow-hidden
                                    ${isSelected 
                                        ? 'border-alsero-first bg-gradient-to-br from-alsero-first/15 to-alsero-first/5 shadow-xl scale-105 ring-2 ring-alsero-first/20' 
                                        : isCurrent
                                        ? 'border-alsero-first bg-gradient-to-br from-alsero-first/10 to-white shadow-lg ring-2 ring-alsero-first/30'
                                        : 'border-gray-200 bg-white hover:border-alsero-first hover:bg-gradient-to-br hover:from-alsero-first/10 hover:to-white hover:shadow-lg'
                                    }
                                    ${!month.isAvailable ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                                `}
                                whileHover={month.isAvailable ? { scale: 1.05, y: -2 } : {}}
                                whileTap={month.isAvailable ? { scale: 0.98 } : {}}
                                disabled={!month.isAvailable}
                            >
                                {isCurrent && (
                                    <motion.div
                                        initial={{ scale: 0, opacity: 0 }}
                                        animate={{ scale: 1, opacity: 1 }}
                                        className="absolute -top-2 -left-2 bg-alsero-first text-white text-xs font-bold px-2 py-1 rounded-full shadow-lg z-20"
                                    >
                                        {getVariable('yacht_price.current_month', 'Current')}
                                    </motion.div>
                                )}
                                
                                <div className={`absolute top-0 right-0 w-20 h-20 rounded-full -mr-10 -mt-10 ${
                                    isCurrent ? 'bg-alsero-first/15' : 'bg-alsero-first/5'
                                }`} />
                                
                                <div className="text-center mb-2 relative z-10">
                                    <div className="text-[10px] font-medium text-alsero-light-gray mb-0.5 uppercase tracking-wide">
                                        {month.name}
                                    </div>
                                    <div className="text-base font-bold text-alsero-dark-gray">
                                        {String(month.num).padStart(2, '0')}
                                    </div>
                                </div>

                                {month.price && (
                                    <div className="text-center relative z-10">
                                        <div className="text-base font-bold text-alsero-first mb-0.5 leading-tight">
                                            {formatPrice(month.price, currency)}
                                        </div>
                                        <div className="text-[10px] text-alsero-light-gray font-medium">
                                            {isWeekly
                                                ? getVariable('yacht_price.per_week', 'per week')
                                                : isMonthly
                                                    ? getVariable('yacht_price.monthly', 'monthly')
                                                    : getVariable('yacht_price.per_day', 'per day')}
                                        </div>
                                    </div>
                                )}

                                {isSelected && (
                                    <motion.div
                                        initial={{ scale: 0, rotate: -180 }}
                                        animate={{ scale: 1, rotate: 0 }}
                                        transition={{ type: "spring", stiffness: 200, damping: 15 }}
                                        className="absolute -top-2 -right-2 w-7 h-7 bg-alsero-first rounded-full flex items-center justify-center shadow-lg ring-2 ring-white"
                                    >
                                        <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                        </svg>
                                    </motion.div>
                                )}


                                {isHovered && !isSelected && month.isAvailable && (
                                    <motion.div
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        className="absolute inset-0 bg-gradient-to-br from-alsero-first/10 to-transparent rounded-xl"
                                    />
                                )}
                            </motion.button>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}
